/**
 * ReceiptFormPage — criar/editar recibo, no desenho oficial de `/quotes/new`.
 *
 * Era o `ReceiptFormModal`. Virou ROTA (`/recibos/novo`, `/recibos/:id/editar`)
 * pelos mesmos motivos do #163 (OS e contrato) e do #164 (financeiro): o desenho
 * oficial é layout de PÁGINA — o hero usa a sangria `-mx-4 -mt-6` do container do
 * `MainLayout` e a barra de ações se ancora na navegação inferior —, o formulário
 * deixa de disputar altura com o `max-h-[90vh]` do diálogo, o botão físico de
 * voltar funciona sozinho e o rascunho passa a ter URL.
 *
 * O QUE MUDA DE RELÓGIO (a armadilha do #163): no diálogo, `open` disparava a
 * hidratação E garantia que o dado JÁ existia na montagem. Aqui a página nasce
 * montada e o recibo chega depois (o cache pode estar frio), então a hidratação
 * acontece UMA vez, atrás de um marcador — sem ele, cada refetch da lista
 * reescreveria o que o usuário está digitando.
 *
 * COMO O CONTEXTO VIAJA: o `prefill` (objeto) vai no STATE da navegação, como no
 * `FinanceEntryFormPage`; ele é conveniência, não autorização — o recibo não
 * muda nenhum outro documento.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { FieldLabel, FIELD_INPUT, CONTENT_CARD } from '@/components/ui/field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, FileText, Hash, ListPlus, Loader2, PenLine, Plus, Trash2, User } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { ClientSelector } from '@/components/quotes/ClientSelector';
import { SignaturePad } from '@/components/quotes/SignaturePad';
import { onlyDigits } from '@/lib/documentUtils';
import { parseBRLInput } from '@/lib/currencyInput';
import { MoneyInput } from '@/components/ui/money-input';
import { heroTotalSizeClass } from '@/lib/quoteHero';
import { cn } from '@/lib/utils';
import { PAYMENT_METHOD_LABELS } from '@/lib/financeLabels';
import { numeroParaReais } from '@/lib/valorExtenso';
import {
  receiptAmountFrom,
  receiptItemFromRow,
  receiptItemLineTotal,
  type ReceiptItemForm,
} from '@/lib/receiptItems';
import { formatQuantityInput, parseQuantityInput } from '@/lib/quantity';
import { formatBRL } from '@/lib/financeLabels';
import { createUuid } from '@/lib/uuid';
import {
  useCreateReceipt,
  useReceipts,
  useUpdateReceipt,
  todayISODate,
  type ReceiptInput,
  type ReceiptPrefill,
} from '@/hooks/useReceipts';
import type { Client, PaymentMethod } from '@/types/database';

const PAYMENT_METHODS = Object.keys(PAYMENT_METHOD_LABELS) as PaymentMethod[];

/** O que os chamadores empurram para esta rota pelo STATE da navegação. */
export interface ReceiptRouteState {
  prefill?: ReceiptPrefill | null;
}

const brl = (n: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);

export default function ReceiptFormPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const prefill = (location.state as ReceiptRouteState | null)?.prefill ?? null;

  /** Fechar = voltar para de onde veio (lista, orçamento, contrato, OS…). */
  const close = () => navigate(-1);

  const { profile } = useAuth();
  const { activeCompany } = useCompany();

  // A entidade em edição vem da LISTA já cacheada (offline-first), do mesmo
  // jeito que OS, contrato e lançamento: o formulário não faz consulta própria.
  const { receipts, isLoading: loadingReceipts } = useReceipts();
  const receipt = id ? (receipts.find((r: any) => r.id === id) ?? null) : null;
  const isEditing = Boolean(receipt);

  const createReceipt = useCreateReceipt();
  const updateReceipt = useUpdateReceipt();

  const [showClientSelector, setShowClientSelector] = useState(false);
  const [showQuotePicker, setShowQuotePicker] = useState(false);

  // Vínculos (ocultos — vêm do prefill / do picker de orçamento / da edição).
  const [clientId, setClientId] = useState<string | null>(null);
  const [quoteId, setQuoteId] = useState<string | null>(null);
  const [serviceOrderId, setServiceOrderId] = useState<string | null>(null);
  const [financeEntryId, setFinanceEntryId] = useState<string | null>(null);

  const [payerName, setPayerName] = useState('');
  const [payerDocument, setPayerDocument] = useState('');
  const [payerAddress, setPayerAddress] = useState('');
  // `null` = vazio (mostra o placeholder), nunca 0. Campo MASCARADO (#157):
  // os dígitos entram pela direita, em centavos, então não há separador para
  // digitar e nada a adivinhar. O estado guarda NÚMERO — o texto é assunto do
  // `MoneyInput`.
  const [amount, setAmount] = useState<number | null>(null);
  const [description, setDescription] = useState('');
  /**
   * Linhas discriminadas (lacuna L1). Lista VAZIA = recibo sem discriminação,
   * que é o estado de todo o histórico e o padrão de um recibo novo.
   *
   * Havendo linhas, o `amount` passa a ser a SOMA delas e o campo de valor
   * vira somente leitura — dois números editáveis para a mesma coisa divergem,
   * e o do papel tem que ser o da coluna impressa (#69).
   */
  const [items, setItems] = useState<ReceiptItemForm[]>([]);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | ''>('');
  /**
   * Formas USADAS (não "aceitas", como no orçamento): um pagamento pode ser
   * DIVIDIDO — R$ 300 no Pix e R$ 200 em dinheiro —, e a coluna singular
   * sozinha torna isso impossível de registrar.
   */
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  /** Como o pagamento foi combinado ("3x sem juros", "à vista"). */
  const [paymentTerms, setPaymentTerms] = useState('');
  const [issueDate, setIssueDate] = useState(todayISODate());
  const [issuePlace, setIssuePlace] = useState('');
  const [notes, setNotes] = useState('');

  // Assinatura opcional do emitente.
  const [wantSignature, setWantSignature] = useState(false);
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [signerName, setSignerName] = useState('');

  /**
   * Hidrata UMA vez. No diálogo o gatilho era `open` (e o dado já existia);
   * aqui a página nasce montada e o recibo chega depois — sem o marcador, cada
   * refetch da lista reescreveria o que está sendo digitado.
   */
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    // Edição: espera o recibo chegar (cache frio, link direto).
    if (id && !receipt) return;
    hydratedRef.current = true;
    if (receipt) {
      setClientId(receipt.client_id ?? null);
      setQuoteId(receipt.quote_id ?? null);
      setServiceOrderId(receipt.service_order_id ?? null);
      setFinanceEntryId(receipt.finance_entry_id ?? null);
      setPayerName(receipt.payer_name ?? '');
      setPayerDocument(receipt.payer_document ?? '');
      setPayerAddress(receipt.payer_address ?? '');
      // Pode vir STRING do PostgREST ("250.50"): `parseBRLInput` trata string
      // sem vírgula como número cru (#123a).
      setAmount(receipt.amount == null ? null : parseBRLInput(receipt.amount));
      setDescription(receipt.description ?? '');
      // Metade da LEITURA (#70): sem ela, editar qualquer campo do recibo
      // salvaria com a lista vazia e APAGARIA as linhas — o save daria certo.
      setItems(
        (Array.isArray(receipt.receipt_items) ? receipt.receipt_items : [])
          .slice()
          .sort((a: any, b: any) => Number(a?.position ?? 0) - Number(b?.position ?? 0))
          .map((row: any, index: number) => receiptItemFromRow(row, index)),
      );
      setPaymentMethod((receipt.payment_method as PaymentMethod) ?? '');
      setPaymentMethods(
        Array.isArray(receipt.payment_methods) ? (receipt.payment_methods as PaymentMethod[]) : [],
      );
      setPaymentTerms(receipt.payment_terms ?? '');
      setIssueDate(receipt.issue_date ?? todayISODate());
      setIssuePlace(receipt.issue_place ?? '');
      setNotes(receipt.notes ?? '');
      setWantSignature(Boolean(receipt.issuer_signature));
      setSignatureData(receipt.issuer_signature ?? null);
      setSignerName(receipt.issuer_signer_name ?? '');
      return;
    }
    const p = prefill ?? {};
    setClientId(p.client_id ?? null);
    setQuoteId(p.quote_id ?? null);
    setServiceOrderId(p.service_order_id ?? null);
    setFinanceEntryId(p.finance_entry_id ?? null);
    setPayerName(p.payer_name ?? '');
    setPayerDocument(p.payer_document ?? '');
    setPayerAddress(p.payer_address ?? '');
    setAmount(p.amount == null ? null : parseBRLInput(p.amount));
    setDescription(p.description ?? '');
    setPaymentMethod((p.payment_method as PaymentMethod) ?? '');
    setIssueDate(todayISODate());
    setIssuePlace((activeCompany as any)?.pix_city ?? '');
    setNotes('');
    setWantSignature(false);
    setSignatureData(null);
    setSignerName((profile as any)?.full_name || activeCompany?.name || '');
    // `prefill` vem do state da navegação e é lido na entrada.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, receipt]);

  /*
   * O valor do recibo. Com linhas, ele é a SOMA delas — a MESMA função que o
   * save usa (`receiptAmountFrom`), para a tela nunca mostrar um número e o
   * banco guardar outro. Sem linhas, é o que a pessoa digitou.
   */
  const amountFromItems = useMemo(() => receiptAmountFrom(items), [items]);
  const hasItems = amountFromItems !== null;
  const parsedAmount = amountFromItems ?? amount ?? 0;
  const extensoPreview = useMemo(
    () => (parsedAmount > 0 ? numeroParaReais(parsedAmount) : ''),
    [parsedAmount],
  );

  const handleSelectClient = (client: Client) => {
    setClientId(client.id);
    setPayerName(client.name ?? '');
    setPayerDocument((client as any).document ?? '');
    setPayerAddress(client.address ?? '');
  };

  const handleSubmit = async () => {
    if (!payerName.trim()) {
      toast.warning('Informe quem pagou (o pagador).');
      return;
    }
    if (parsedAmount <= 0) {
      toast.warning('Informe o valor recebido.');
      return;
    }

    // A assinatura só é gravada se o usuário optou por assiná-la.
    const issuerSignature = wantSignature ? signatureData : null;
    const issuerSignerName = wantSignature ? signerName.trim() || null : null;

    const payload: ReceiptInput = {
      quote_id: quoteId,
      service_order_id: serviceOrderId,
      client_id: clientId,
      finance_entry_id: financeEntryId,
      payer_name: payerName.trim() || null,
      // Só dígitos (convenção em clients/quotes/contracts).
      payer_document: onlyDigits(payerDocument) || null,
      payer_address: payerAddress.trim() || null,
      amount: parsedAmount,
      // As linhas viajam SEMPRE (mesmo vazias): `undefined` significaria "não
      // mexa", e aí remover a última linha nunca chegaria ao banco.
      items,
      description: description.trim() || null,
      payment_method: paymentMethod || null,
      // `[]` vira null: no recibo, lista vazia e "não informado" são a mesma
      // coisa (quem paga de uma forma só usa o campo singular acima).
      payment_methods: paymentMethods.length > 0 ? paymentMethods : null,
      payment_terms: paymentTerms.trim() || null,
      issue_date: issueDate || todayISODate(),
      issue_place: issuePlace.trim() || null,
      issuer_signature: issuerSignature,
      issuer_signer_name: issuerSignerName,
      notes: notes.trim() || null,
    };

    try {
      if (isEditing && receipt) {
        await updateReceipt.mutateAsync({ id: receipt.id, updates: payload });
        toast.success('Recibo atualizado.');
        close();
      } else {
        const res = await createReceipt.mutateAsync(payload);
        toast.success((res as any)?.offline ? 'Recibo salvo localmente.' : 'Recibo criado.');
        // `replace`: o formulário já cumpriu o papel dele, e voltar do recibo
        // recém-criado tem que devolver a tela de ORIGEM, não o rascunho.
        if (res?.id) navigate(`/recibos/${res.id}`, { replace: true });
        else close();
      }
    } catch {
      // toast tratado no onError da mutation
    }
  };

  /* ---- Itens do recibo (lacuna L1) ---------------------------------- */
  const addReceiptItem = () => {
    setItems((prev) => [
      ...prev,
      {
        // `createUuid` e não o índice: o `key` do React tem que sobreviver a
        // remover uma linha do meio, senão o React reaproveita o input errado.
        id: createUuid(),
        name: '',
        quantity: 1,
        unit_price: 0,
        discount: 0,
        discount_type: 'value',
      },
    ]);
  };

  const updateReceiptItem = (itemId: string, patch: Partial<ReceiptItemForm>) => {
    setItems((prev) => prev.map((it) => (it.id === itemId ? { ...it, ...patch } : it)));
  };

  const removeReceiptItem = (itemId: string) => {
    setItems((prev) => prev.filter((it) => it.id !== itemId));
  };

  const saving = createReceipt.isPending || updateReceipt.isPending;

  /* ==================================================================== */
  /* HERO — o mesmo cabeçalho sangrado de /quotes/new.                     */
  /* -------------------------------------------------------------------- */
  /* O recibo é um DOCUMENTO com valor, então o valor ao vivo é o VALOR     */
  /* (nos cadastros é o nome, #160). A sublinha é o PAGADOR: é ele que      */
  /* distingue um recibo do outro na lista.                                */
  const heroAmount = brl(parsedAmount);

  const hero = (
    <div className="relative -mx-4 -mt-6 overflow-hidden rounded-b-[2rem] bg-gradient-to-br from-primary via-primary to-emerald-600 px-4 pb-14 pt-6 text-white shadow-[0_10px_30px_-12px_hsla(var(--primary)/0.6)]">
      <div className="pointer-events-none absolute -right-10 -top-16 h-44 w-44 rounded-full bg-white/10" />
      <div className="pointer-events-none absolute -left-16 top-16 h-40 w-40 rounded-full bg-white/5" />

      <div className="relative flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
          onClick={close}
          aria-label="Voltar"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] uppercase tracking-wide text-white/70">
            {isEditing ? 'Editando' : 'Novo documento'}
          </p>
          <h1 className="truncate text-lg font-bold leading-tight">Recibo</h1>
        </div>
        {/* Número: só existe depois de gravado (o servidor numera no insert). */}
        {isEditing && !!receipt?.sequence_id && (
          <div className="flex shrink-0 items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1">
            <Hash className="h-3.5 w-3.5 text-white/80" />
            <span className="font-mono text-sm font-bold leading-none">
              {String(receipt.sequence_id).padStart(4, '0')}
            </span>
          </div>
        )}
      </div>

      <div className="relative mt-5">
        <p className="text-xs text-white/75">Valor recebido</p>
        <p className={cn('truncate font-bold leading-tight', heroTotalSizeClass(heroAmount))}>
          {heroAmount}
        </p>
        <p className="mt-1 truncate text-xs text-white/70">
          {payerName.trim() || 'Preencha os dados abaixo'}
        </p>
      </div>
    </div>
  );

  // Edição de um recibo que ainda não chegou (cache frio, link direto).
  if (id && !receipt && loadingReceipts) {
    return (
      <MainLayout showFab={false} width="form">
        <div className="pb-4">
          {hero}
          <div className="relative z-10 -mt-9 flex justify-center rounded-2xl border border-border/60 bg-card py-16 shadow-sm">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </div>
      </MainLayout>
    );
  }

  if (id && !receipt) {
    return (
      <MainLayout showFab={false} width="form">
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <h1 className="text-2xl font-bold text-muted-foreground">Recibo não encontrado</h1>
          <Button3D className="mt-6" onClick={() => navigate('/recibos')}>
            Ver recibos
          </Button3D>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout showFab={false} width="form">
      <div className="pb-4">
        {hero}

        {/* Conteúdo — o primeiro cartão sobe e encosta na curva do hero */}
        <div className="relative z-10 -mt-9 space-y-5">
          {/* ── PAGADOR ─────────────────────────────────────────────────── */}
          <div className={cn(CONTENT_CARD, 'space-y-4 p-4')}>
            {!isEditing && (
              <Button
                type="button"
                variant="outline"
                className="w-full justify-start rounded-xl"
                onClick={() => setShowQuotePicker(true)}
              >
                <FileText className="mr-2 h-4 w-4" />
                Gerar a partir de um orçamento
              </Button>
            )}

            <div className="space-y-1.5">
              <FieldLabel htmlFor="rec-payer" icon={User}>
                Pagador (quem pagou)
              </FieldLabel>
              <div className="flex items-center gap-2">
                <Input
                  id="rec-payer"
                  value={payerName}
                  onChange={(e) => setPayerName(e.target.value)}
                  placeholder="Nome de quem pagou"
                  className={cn(FIELD_INPUT, 'min-w-0 flex-1')}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 shrink-0 rounded-xl"
                  onClick={() => setShowClientSelector(true)}
                  aria-label="Escolher um cliente cadastrado"
                  title="Escolher um cliente cadastrado"
                >
                  <User className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 items-start gap-3">
              <div className="space-y-1.5">
                <FieldLabel htmlFor="rec-doc">CPF/CNPJ (opcional)</FieldLabel>
                <Input
                  id="rec-doc"
                  value={payerDocument}
                  onChange={(e) => setPayerDocument(e.target.value)}
                  placeholder="Somente números"
                  className={cn(FIELD_INPUT, 'min-w-0')}
                />
              </div>
              <div className="space-y-1.5">
                <FieldLabel htmlFor="rec-address">Endereço (opcional)</FieldLabel>
                <Input
                  id="rec-address"
                  value={payerAddress}
                  onChange={(e) => setPayerAddress(e.target.value)}
                  placeholder="Rua, nº, bairro"
                  className={cn(FIELD_INPUT, 'min-w-0')}
                />
              </div>
            </div>
          </div>

          {/* ── VALOR E REFERÊNCIA ──────────────────────────────────────── */}
          <div className={cn(CONTENT_CARD, 'space-y-4 p-4')}>
            {/* ================================================================ */}
            {/* ITENS — discriminação opcional do que foi pago (lacuna L1)        */}
            {/* ================================================================ */}
            {/* Lista vazia = recibo como sempre foi (valor + "referente a"). Com
                linhas, o valor passa a ser a soma e o campo acima vira leitura. */}
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <FieldLabel icon={ListPlus}>Itens (opcional)</FieldLabel>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addReceiptItem}
                  className="h-8"
                >
                  <Plus className="mr-1 h-3.5 w-3.5" />
                  Adicionar
                </Button>
              </div>

              {items.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  Sem itens, o recibo sai com o valor e o campo "Referente a".
                  Com itens, ele lista cada serviço e soma o total.
                </p>
              ) : (
                <div className="space-y-2">
                  {items.map((item, index) => (
                    <div
                      key={item.id}
                      className="space-y-2 rounded-lg border border-dashed border-primary/40 p-2.5"
                    >
                      <div className="flex items-start gap-2">
                        <Input
                          aria-label={`Descrição do item ${index + 1}`}
                          placeholder="Ex.: Instalação elétrica"
                          value={item.name}
                          onChange={(e) => updateReceiptItem(item.id, { name: e.target.value })}
                          className={cn(FIELD_INPUT, 'min-w-0 flex-1')}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 shrink-0 text-destructive"
                          onClick={() => removeReceiptItem(item.id)}
                          aria-label={`Remover item ${index + 1}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-3 items-end gap-2">
                        <div className="space-y-1">
                          <FieldLabel>Qtd</FieldLabel>
                          <Input
                            aria-label={`Quantidade do item ${index + 1}`}
                            inputMode="decimal"
                            value={formatQuantityInput(item.quantity)}
                            onChange={(e) => {
                              const parsed = parseQuantityInput(e.target.value);
                              if (!parsed.ok) return;
                              updateReceiptItem(item.id, { quantity: parsed.value ?? 0 });
                            }}
                            className={cn(FIELD_INPUT, 'min-w-0')}
                          />
                        </div>
                        <div className="col-span-2 space-y-1">
                          <FieldLabel>Valor unitário</FieldLabel>
                          <MoneyInput
                            aria-label={`Valor unitário do item ${index + 1}`}
                            value={item.unit_price || null}
                            onCommit={(v) => updateReceiptItem(item.id, { unit_price: v ?? 0 })}
                            className="min-w-0"
                          />
                        </div>
                      </div>

                      <p className="text-right text-xs font-semibold text-muted-foreground">
                        {formatBRL(receiptItemLineTotal(item))}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <FieldLabel htmlFor="rec-amount">Valor recebido (R$)</FieldLabel>
              {/* O `className` do MoneyInput vai para o WRAPPER, não para o
                  input — o `FIELD_INPUT` seria redundante (o componente já o
                  aplica por dentro). */}
              {hasItems ? (
                <>
                  {/* Somente leitura de propósito: com linhas, o valor É a soma
                      delas. Deixar os dois editáveis criaria um documento cujo
                      rodapé não fecha com a própria coluna (#69). */}
                  <div
                    className={cn(FIELD_INPUT, 'flex items-center font-semibold')}
                    aria-live="polite"
                  >
                    {formatBRL(parsedAmount)}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Somado dos {items.length} {items.length === 1 ? 'item' : 'itens'} acima.
                  </p>
                </>
              ) : (
                <MoneyInput id="rec-amount" value={amount} onCommit={setAmount} className="min-w-0" />
              )}
              {extensoPreview && (
                <p className="rounded-xl bg-muted px-3 py-2 text-xs capitalize text-muted-foreground">
                  {extensoPreview}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <FieldLabel htmlFor="rec-desc">Referente a</FieldLabel>
              <Textarea
                id="rec-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Ex.: serviços de instalação elétrica"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 items-start gap-3">
              <div className="space-y-1.5">
                <FieldLabel htmlFor="rec-method">Forma de pagamento</FieldLabel>
                <Select
                  value={paymentMethod || 'none'}
                  onValueChange={(v) => setPaymentMethod(v === 'none' ? '' : (v as PaymentMethod))}
                >
                  <SelectTrigger id="rec-method" className={cn(FIELD_INPUT, 'min-w-0')}>
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {PAYMENT_METHODS.map((m) => (
                      <SelectItem key={m} value={m}>
                        {PAYMENT_METHOD_LABELS[m]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <FieldLabel htmlFor="rec-date">Data de emissão</FieldLabel>
                <Input
                  id="rec-date"
                  type="date"
                  value={issueDate}
                  onChange={(e) => setIssueDate(e.target.value)}
                  className={cn(FIELD_INPUT, 'min-w-0')}
                />
              </div>
            </div>

            {/* CONDIÇÕES combinadas (lacuna L15). Sai no PDF logo abaixo da
                forma de pagamento; vazio não desenha nada. */}
            <div className="space-y-1.5">
              <FieldLabel htmlFor="rec-terms">Condições de pagamento (opcional)</FieldLabel>
              <Input
                id="rec-terms"
                value={paymentTerms}
                onChange={(e) => setPaymentTerms(e.target.value)}
                placeholder="Ex.: à vista · 3x sem juros"
                className={cn(FIELD_INPUT, 'min-w-0')}
              />
            </div>

            {/* PAGAMENTO DIVIDIDO. Marcar mais de uma forma é o único jeito de
                registrar "R$ 300 no Pix + R$ 200 em dinheiro" — com a forma
                singular acima isso se perde. Nenhuma marcada = usa a singular. */}
            <div className="space-y-1.5">
              <FieldLabel>Pagamento dividido (opcional)</FieldLabel>
              <div className="flex flex-wrap gap-1.5">
                {PAYMENT_METHODS.map((m) => {
                  const ativo = paymentMethods.includes(m);
                  return (
                    <button
                      key={m}
                      type="button"
                      onClick={() =>
                        setPaymentMethods((prev) =>
                          prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m],
                        )
                      }
                      aria-pressed={ativo}
                      className={cn(
                        'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                        ativo
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border text-muted-foreground hover:bg-muted',
                      )}
                    >
                      {PAYMENT_METHOD_LABELS[m]}
                    </button>
                  );
                })}
              </div>
              {paymentMethods.length > 1 && (
                <p className="text-xs text-muted-foreground">
                  O recibo vai imprimir:{' '}
                  {paymentMethods.map((m) => PAYMENT_METHOD_LABELS[m]).join(' + ')}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <FieldLabel htmlFor="rec-place">Local (cidade)</FieldLabel>
              <Input
                id="rec-place"
                value={issuePlace}
                onChange={(e) => setIssuePlace(e.target.value)}
                placeholder="Ex.: São Paulo"
                className={FIELD_INPUT}
              />
            </div>

            <div className="space-y-1.5">
              <FieldLabel htmlFor="rec-notes">Observações (opcional)</FieldLabel>
              <Textarea
                id="rec-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Anotações internas ou impressas no recibo"
                rows={2}
              />
            </div>
          </div>

          {/* ── ASSINATURA DO EMITENTE (opcional) ───────────────────────── */}
          <div className={cn(CONTENT_CARD, 'space-y-3 p-4')}>
            <div className="flex items-center justify-between gap-2">
              <FieldLabel icon={PenLine}>Minha assinatura (opcional)</FieldLabel>
              <Button
                type="button"
                variant={wantSignature ? 'secondary' : 'outline'}
                size="sm"
                className="shrink-0 rounded-xl"
                onClick={() => setWantSignature((v) => !v)}
              >
                {wantSignature ? 'Remover' : 'Adicionar'}
              </Button>
            </div>
            {wantSignature && (
              <div className="space-y-3 pt-1">
                <Input
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="Nome de quem assina"
                  className={FIELD_INPUT}
                  aria-label="Nome de quem assina"
                />
                <SignaturePad label="Assinatura do emitente" onSignatureChange={setSignatureData} />
              </div>
            )}
          </div>
        </div>

        {/* Barra FLUTUANTE — filha DIRETA do container da página. */}
        <div className="sticky bottom-[calc(var(--bottom-nav-h)+0.5rem)] z-20 mt-6 rounded-2xl border border-border/60 bg-card/95 p-2 shadow-[0_-6px_20px_-10px_rgba(0,0,0,0.35)] backdrop-blur">
          <div className="flex gap-2">
            <Button3D color="gray" size="sm" className="flex-1" onClick={close} disabled={saving}>
              Cancelar
            </Button3D>
            <Button3D size="sm" className="flex-1" onClick={handleSubmit} disabled={saving}>
              {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              {isEditing ? 'Salvar' : 'Criar recibo'}
            </Button3D>
          </div>
        </div>
      </div>

      <ClientSelector
        open={showClientSelector}
        onOpenChange={setShowClientSelector}
        onSelect={handleSelectClient}
      />
      <ReceiptQuotePickerDialog
        open={showQuotePicker}
        onOpenChange={setShowQuotePicker}
        companyId={activeCompany?.id ?? null}
        onPick={(quote, desc) => {
          setQuoteId(quote.id);
          setServiceOrderId(null);
          setFinanceEntryId(null);
          setClientId(quote.client_id ?? null);
          setPayerName(quote.client_name ?? '');
          setPayerDocument(quote.client_document ?? '');
          setPayerAddress(quote.client_address ?? '');
          if (desc) setDescription(desc);
          setAmount(quote.total == null ? null : parseBRLInput(quote.total));
          setShowQuotePicker(false);
        }}
      />
    </MainLayout>
  );
}

/**
 * Picker do orçamento de origem. Lista os aprovados/concluídos da empresa (mais
 * recentes primeiro) e, ao escolher, busca os itens para compor o "referente a".
 *
 * Continua sendo um DIÁLOGO de propósito: é uma escolha pontual dentro do
 * formulário, e transformá-la em rota faria o rascunho do recibo ficar no
 * histórico do navegador entre a pergunta e a resposta.
 */
function ReceiptQuotePickerDialog({
  open,
  onOpenChange,
  companyId,
  onPick,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string | null;
  onPick: (quote: any, description: string) => void;
}) {
  const [quotes, setQuotes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !companyId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data } = await supabase
        .from('quotes')
        .select('*')
        .eq('company_id', companyId)
        .in('status', ['approved', 'completed'])
        .order('created_at', { ascending: false })
        .limit(50);
      if (!cancelled) {
        setQuotes((data ?? []) as any[]);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, companyId]);

  const pick = async (quote: any) => {
    const { data: items } = await supabase
      .from('quote_items')
      .select('name')
      .eq('quote_id', quote.id)
      .order('position', { ascending: true });
    const description = (items ?? [])
      .map((i: any) => i.name)
      .filter(Boolean)
      .join(', ');
    onPick(quote, description || quote.title || '');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Selecione um orçamento</DialogTitle>
        </DialogHeader>
        {loading ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Carregando…</p>
        ) : quotes.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Nenhum orçamento aprovado ou concluído encontrado.
          </p>
        ) : (
          <div className="space-y-2">
            {quotes.map((q) => (
              <button
                key={q.id}
                type="button"
                onClick={() => pick(q)}
                className="w-full rounded-md border p-3 text-left transition hover:bg-muted"
              >
                <p className="font-medium">{q.title || q.client_name || 'Orçamento'}</p>
                <p className="text-xs text-muted-foreground">
                  {q.client_name ? `${q.client_name} · ` : ''}
                  {brl(Number(q.total ?? 0))}
                </p>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
